/**
 * Attribute-family parts: `attr=`, `.property=`, `?boolean=` and `@event=`.
 *
 * The kind of an attribute part is fixed by the compiled spec — nothing
 * assigns to `spec.type`, and one spec is shared by every clone of its
 * template — so `createAttributePart` takes that branch ONCE, at construction,
 * and the part it returns commits through exactly one function.
 *
 * Those commit functions are module-level and shared by every part of their
 * kind: the part carries its element, its attribute name and its memo as
 * fields, and each commit reads them from `this`. Written as per-part closures
 * they were one JSFunction plus one V8 context per part — 64 bytes each, three
 * attribute parts per row of a keyed list, and nothing in them distinguishable
 * from the next part's.
 *
 * Split out of `render-dom-parts.ts`, which owns marker discovery, element
 * parts and node parts and had grown past both the file-length guideline and
 * the per-module gzip budget.
 */
import { assertNeverPartType, isLive, resolveValue, splitCompositeSegments, } from './render-values.js';
import { normalizeAttributeName } from './render-template.js';
import { safeUrlAttribute } from '../ui/html-escape.js';
/** Sentinel distinguishing "attribute explicitly removed" from "never committed". */
export const REMOVED = Symbol('hydrogen.attributeRemoved');
function isEventListenerValue(value) {
    if (typeof value === 'function')
        return true;
    return !!(value &&
        typeof value === 'object' &&
        typeof value.handleEvent === 'function');
}
function extractEventOptions(value) {
    if (typeof value === 'function')
        return undefined;
    const candidate = value;
    const options = {};
    let has = false;
    if (typeof candidate.capture === 'boolean') {
        options.capture = candidate.capture;
        has = true;
    }
    if (typeof candidate.once === 'boolean') {
        options.once = candidate.once;
        has = true;
    }
    if (typeof candidate.passive === 'boolean') {
        options.passive = candidate.passive;
        has = true;
    }
    return has ? options : undefined;
}
function optionsEqual(a, b) {
    return !!a?.capture === !!b?.capture && !!a?.once === !!b?.once && !!a?.passive === !!b?.passive;
}
export function commitEventListener(element, eventName, rawValue, memo) {
    const nextListener = isEventListenerValue(rawValue) ? rawValue : null;
    const nextOptions = nextListener ? extractEventOptions(nextListener) : undefined;
    if (memo.listener === nextListener && optionsEqual(memo.options, nextOptions))
        return;
    if (memo.listener) {
        element.removeEventListener(eventName, memo.listener, memo.options);
    }
    if (nextListener) {
        element.addEventListener(eventName, nextListener, nextOptions);
    }
    memo.listener = nextListener;
    memo.options = nextOptions;
}
// ---------------------------------------------------------------------------
// Attribute-family parts: attribute (single-hole and composite), property,
// boolean-attribute, event. Every branch memoizes its last committed state
// (G5) and unwraps `live()` for property/attribute by comparing against the
// element's current DOM state instead of the memo (G9).
// ---------------------------------------------------------------------------
export function unwrapLive(resolved) {
    return isLive(resolved) ? resolveValue(resolved.value) : resolved;
}
/** Shared no-op standing in for the part methods an attribute kind does not need. */
export function noAttributeWork() {
    // A part with no element, and the destroy of every attribute kind that holds
    // nothing to release, share this instead of allocating a closure each.
}
function commitEventAttribute(value) {
    commitEventListener(this.element, this.spec.eventName, resolveValue(value), this.memo);
}
function destroyEventAttribute() {
    const { memo } = this;
    if (!memo.listener)
        return;
    this.element.removeEventListener(this.spec.eventName, memo.listener, memo.options);
    memo.listener = null;
}
function eventPart(spec, element, attrName) {
    return {
        spec,
        element,
        attrName,
        memo: { listener: null, options: undefined },
        update: commitEventAttribute,
        destroy: destroyEventAttribute,
    };
}
/**
 * The committed value is `inner ?? ''`, so it is never `undefined` — which
 * makes `undefined` a free "never committed" memo. No second field, and no
 * sentinel, is needed to say it: the first commit's `Object.is` against
 * `undefined` is false for every value this can produce.
 */
function commitPropertyAttribute(value) {
    const { attrName } = this;
    const target = this.element;
    const rawValue = resolveValue(value);
    const liveMode = isLive(rawValue);
    const inner = liveMode ? resolveValue(rawValue.value) : rawValue;
    const nextValue = inner ?? '';
    // `live()` compares against the property itself, so an external write is
    // corrected rather than skipped (G9).
    const current = liveMode ? target[attrName] : this.memo;
    this.memo = nextValue;
    if (Object.is(current, nextValue))
        return;
    target[attrName] = nextValue;
}
function commitBooleanAttribute(value) {
    // `live()` is out of spec scope for boolean-attribute (G9 only names
    // property/attribute), but its wrapper object must still be unwrapped
    // before the truthiness check — otherwise `?attr=${live(x)}` always
    // reads as truthy (a non-null wrapper object), never reflecting a falsy
    // `x`, regardless of the memo below.
    const { element, attrName } = this;
    const next = !!unwrapLive(resolveValue(value));
    if (this.memo === next)
        return;
    if (next)
        element.setAttribute(attrName, '');
    else
        element.removeAttribute(attrName);
    this.memo = next;
}
/**
 * `null`, `undefined` and `false` remove the attribute; anything else is
 * stringified between the authored prefix and suffix. `live()` compares
 * against the DOM instead of the memo, so an external write to the attribute
 * is corrected rather than skipped (G9).
 */
function commitSingleAttribute(value) {
    const { element, attrName } = this;
    const rawValue = resolveValue(value);
    const inner = unwrapLive(rawValue);
    const next = inner === null || inner === undefined || inner === false
        ? null
        : `${this.prefix}${String(inner)}${this.suffix}`;
    // `getAttribute` answers null for an absent attribute, which is the same
    // "removed" this memo records — so live and memoised comparison are the one
    // expression. `undefined` (never committed) equals neither, so a first
    // commit always writes.
    const current = isLive(rawValue) ? element.getAttribute(attrName) : this.memo;
    this.memo = next;
    if (current === next)
        return;
    if (next === null) {
        element.removeAttribute(attrName);
        return;
    }
    // URL-bearing values with executable schemes collapse to '' before they can
    // reach the DOM (#1524).
    element.setAttribute(attrName, safeUrlAttribute(attrName, next));
}
function commitCompositeAttribute(value) {
    const { segments } = this;
    const rawValues = value;
    let joined = segments[0] ?? '';
    // An indexed loop, not `forEach`: the callback would be a fresh closure
    // on every commit of every composite attribute.
    for (let index = 0; index < rawValues.length; index += 1) {
        const holeValue = unwrapLive(resolveValue(rawValues[index]));
        const piece = holeValue === null || holeValue === undefined || holeValue === false ? '' : String(holeValue);
        joined += piece + (segments[index + 1] ?? '');
    }
    // Composites never remove the attribute — an empty joined string is a
    // legitimate committed value, not an absence.
    if (this.memo === joined)
        return;
    this.element.setAttribute(this.attrName, safeUrlAttribute(this.attrName, joined));
    this.memo = joined;
}
/**
 * Binds one attribute-family marker to a part that commits through exactly
 * one closure.
 *
 * The kind of an attribute part is fixed for its whole life. `spec.type` is
 * the discriminant of a `PartSpec` built once when the template was compiled
 * and shared by every clone of it; nothing in the runtime assigns to it, and
 * whether an `attribute` spec is composite is fixed the same way by
 * `bindingIndices`. So the branch is taken once, here, rather than on every
 * commit.
 *
 * This used to allocate all five commit closures plus an event memo for every
 * part and then dispatch to one of them by `spec.type` on each update: 15
 * closures per row of the 1,000-row benchmark, twelve of them unreachable
 * from the moment they were created.
 */
export function createAttributePart(spec, element, markerName, markerValue) {
    const rawName = ('rawName' in spec ? spec.rawName : undefined) ||
        markerName ||
        ('attrName' in spec ? spec.attrName : undefined) ||
        '';
    const attrName = ('attrName' in spec ? spec.attrName : undefined) || normalizeAttributeName(rawName);
    // The authored marker attribute never reaches the page: it goes when the
    // normalised name differs from it (`.prop`, `?bool`, `@event`, a cased
    // attribute), and unconditionally for every non-`attribute` kind, whose
    // marker is not an attribute value the browser should keep.
    if (element && rawName && (rawName !== attrName || spec.type !== 'attribute')) {
        element.removeAttribute(rawName);
    }
    // A marker with no element left to bind: the part exists, and does nothing.
    if (!element)
        return { spec, update: noAttributeWork, destroy: noAttributeWork };
    const partType = spec.type;
    switch (partType) {
        case 'event': // ubs:ignore -- this case returns, so no break is required
            return eventPart(spec, element, attrName);
        case 'property': // ubs:ignore -- this case returns, so no break is required
            return propertyPart(spec, element, attrName);
        case 'boolean-attribute': // ubs:ignore -- this case returns, so no break is required
            return booleanPart(spec, element, attrName);
        case 'attribute': // ubs:ignore -- this case returns, so no break is required
            // `attr="${x}"` and `attr="a ${x} b ${y}"` differ by the compiled spec,
            // not by the value: `bindingIndices` is what says the attribute has more
            // than one hole, and it is fixed when the template was compiled.
            return createAttributeValuePart(spec, element, attrName, markerValue || spec.marker);
        default:
            return assertNeverPartType(partType);
    }
}
function propertyPart(spec, element, attrName) {
    return {
        spec,
        element,
        attrName,
        memo: undefined,
        update: commitPropertyAttribute,
        destroy: noAttributeWork,
    };
}
function booleanPart(spec, element, attrName) {
    return {
        spec,
        element,
        attrName,
        memo: undefined,
        update: commitBooleanAttribute,
        destroy: noAttributeWork,
    };
}
function createAttributeValuePart(spec, element, attrName, sourceValue) {
    const { bindingIndices } = spec;
    if (bindingIndices) {
        return {
            spec,
            element,
            attrName,
            segments: splitCompositeSegments(sourceValue, bindingIndices),
            memo: undefined,
            update: commitCompositeAttribute,
            destroy: noAttributeWork,
        };
    }
    const markerIndex = sourceValue.indexOf(spec.marker);
    const prefix = markerIndex >= 0 ? sourceValue.slice(0, markerIndex) : '';
    const suffix = markerIndex >= 0 ? sourceValue.slice(markerIndex + spec.marker.length) : '';
    return {
        spec,
        element,
        attrName,
        prefix,
        suffix,
        memo: undefined,
        update: commitSingleAttribute,
        destroy: noAttributeWork,
    };
}
/**
 * Commits a static binding straight to the element, with no part object to
 * retain (#1398). The value is a primitive by the static allow-list, so
 * `resolveValue` and `live()` unwrapping are identities.
 *
 * The element is the memo (G9): every branch answers from the DOM whether the
 * write is needed, so a repeated static binding — a slot is re-committed on
 * every update walk, including the null → `undefined` sequence the removal
 * memo test pins — costs a read, not a redundant DOM write.
 *
 * `sourceValue` is the clone's marker-attribute text (the plan entry's), the
 * same string `createAttributeValuePart` slices the prefix and suffix from;
 * it is re-sliced per commit instead of retained per part.
 */
export function commitStaticAttributeValue(spec, element, sourceValue, value) {
    const attrName = spec.attrName;
    if (spec.type === 'property') {
        const nextValue = value ?? '';
        const target = element;
        // The same comparison the live part path uses (`commitPropertyAttribute`):
        // `!==` rewrites NaN on every commit and treats -0/+0 as one value (#1442).
        if (!Object.is(target[attrName], nextValue))
            target[attrName] = nextValue;
        return;
    }
    if (spec.type === 'boolean-attribute') {
        const next = !!value;
        // `getAttribute` is the shim-guaranteed presence probe (absent -> null);
        // `hasAttribute` is not part of the render DOM contract.
        const present = element.getAttribute(attrName) !== null;
        if (next !== present) {
            if (next)
                element.setAttribute(attrName, '');
            else
                element.removeAttribute(attrName);
        }
        return;
    }
    if (value === null || value === undefined || value === false) {
        if (element.getAttribute(attrName) !== null)
            element.removeAttribute(attrName);
        return;
    }
    const markerIndex = sourceValue.indexOf(spec.marker);
    const prefix = markerIndex >= 0 ? sourceValue.slice(0, markerIndex) : '';
    const suffix = markerIndex >= 0 ? sourceValue.slice(markerIndex + spec.marker.length) : '';
    const next = safeUrlAttribute(attrName, `${prefix}${String(value)}${suffix}`);
    if (element.getAttribute(attrName) !== next)
        element.setAttribute(attrName, next);
}
/**
 * Strips the authored marker attribute a static attribute-family slot would
 * otherwise leave in the page — the same rule `createAttributePart` applies
 * for live parts (#1398). Normalised single-hole attribute markers keep
 * theirs: the static commit overwrites the attribute wholesale anyway.
 */
export function stripStaticMarkerAttribute(spec, element, markerName) {
    const rawName = ('rawName' in spec ? spec.rawName : undefined) || markerName || '';
    const attrName = ('attrName' in spec ? spec.attrName : undefined) || normalizeAttributeName(rawName);
    if (rawName && (rawName !== attrName || spec.type !== 'attribute')) {
        element.removeAttribute(rawName);
    }
}
//# sourceMappingURL=render-dom-attribute-parts.js.map